import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ApiError, type ApiErrorBody, type AuthState, type AuthTokens, type AuthUser } from './types'
import { resolveApiRoot } from '../config/apiOrigin'
import {
  clearCachedUser,
  clearRefreshToken,
  readCachedUser,
  readRefreshToken,
  writeCachedUser,
  writeRefreshToken,
} from './storage'

export interface AuthProviderProps {
  children: ReactNode
  /**
   * Where the API is.
   *
   * **Passed in, never read from `import.meta.env` here.** `import.meta` is a
   * bundler-specific global and `app_design` has to stay importable from React Native —
   * the same reason `resolveSiteLinks` takes an env record rather than reading one.
   */
  apiRoot?: string
  /**
   * Which app this is — `partners`, `garage`, `resort`, `steward` or `foundation`.
   *
   * Sent as `X-App-Id` on every request. The API records it and uses it for
   * **presentation**: an email's footer links to the *other* King of Kings sites, so it has
   * to know which one it is speaking for.
   *
   * **It is not a credential and grants nothing.** A caller can write anything here; every
   * authorisation decision is made from the bearer token. See `framework/app_context.py`.
   *
   * Optional, and an omitted value is answered as the Foundation — the front door. Each app
   * passes its own in `App.tsx`.
   */
  appId?: string
}

/**
 * Run a refresh with every other tab on this origin held back.
 *
 * ## The bug this exists for
 *
 * Refresh tokens **rotate**: spending one revokes it and issues another, and the API treats
 * a second presentation of a spent token as theft — `reuse_detected` — and ends the entire
 * chain. That is the right call when it is really a theft (see `auth/auth_handler.py`), and
 * it is exactly what two tabs of the same app do to each other by accident.
 *
 * Two tabs open on the board, or one browser restoring three from last time. Both load, both
 * read the same stored token, both post it. The first rotates it. The second presents a
 * token that is now revoked, the API reads that as a stolen session, and **both tabs are
 * signed out** — along with every other device that person is signed in on.
 *
 * The in-flight dedupe on `refreshing` cannot help: it is one variable in one tab.
 *
 * ## Why re-reading storage inside the lock is the whole fix
 *
 * A tab that queues behind another does not repeat the request it wanted to make. It enters
 * the lock, reads the token again, and finds the *new* one the first tab just stored — so it
 * spends a live token rather than a spent one, and nobody is accused of anything.
 *
 * `navigator.locks` is the browser's own primitive for this and is in every current engine.
 * Where it is missing the behaviour is what it was before: a single tab is unaffected, and
 * two tabs are back to racing. A fallback that invented its own lock out of `localStorage`
 * would be a lock with no way to release one held by a tab that crashed.
 */
function withRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = navigator.locks
  if (!locks) return run()
  return locks.request('kkf.auth.refresh', run) as Promise<T>
}

/**
 * What came of trying to spend the refresh token.
 *
 * Three, not two: see `refresh`. Collapsing `'offline'` into `'refused'` is what signed
 * people out every time their connection dropped.
 */
type RefreshOutcome = 'ok' | 'refused' | 'offline'

const AuthContext = createContext<AuthState | null>(null)

/**
 * Where the API is, when a caller does not say.
 *
 * `resolveApiRoot({})` with no environment: the production origin, because a record with no
 * `DEV` in it is treated as a production build. That is the safe direction for a default —
 * an app that forgets to pass `apiRoot` reaches the real API rather than silently posting to
 * its own origin and parsing an HTML page as JSON. Both apps that mount this pass their own,
 * resolved from `import.meta.env`; see `app_partners/src/data/links.ts`.
 */
const DEFAULT_API_ROOT = resolveApiRoot()

async function parseError(response: Response): Promise<ApiError> {
  // A proxy or an infrastructure error can answer with HTML, so a failed parse must not
  // become a second, more confusing error than the one being reported.
  let body: ApiErrorBody | null = null
  try {
    body = (await response.json()) as ApiErrorBody
  } catch {
    body = null
  }
  return new ApiError(response.status, body)
}

/**
 * Who is signed in, and how to ask the API anything.
 *
 * **One definition for all five front ends**, per ground rule 5 — the Partners signs in
 * business owners and Steward signs in staff, and both need the same token handling, the
 * same refresh, and the same storage guards.
 *
 * ## The access token is in a ref, not in state
 *
 * Two reasons, and the second is the one that bites. React state is asynchronous, so a
 * `request` fired immediately after `signIn` would read the previous value and send no
 * token at all. And a token in state is a token in the React tree, which is one
 * `JSON.stringify` of props away from a log.
 *
 * ## Authorisation is the API's, always
 *
 * `hasRole` exists to **hide** a control the person cannot use — a button that returns 403
 * is worse than no button. It is presentation and nothing more: every actual decision is
 * made by `require_roles` on the server, and a front end that gates on `hasRole` alone is
 * gating on a value it was handed.
 */
export function AuthProvider({ children, apiRoot = DEFAULT_API_ROOT, appId }: AuthProviderProps) {
  /**
   * Who is signed in — seeded from the last-known copy in storage.
   *
   * **So the header is right on the first frame, with no request.** See `storage.ts` on why
   * a copy of the user is kept and why nothing may ever be authorised from it. The API is
   * asked in the background on every load and this is corrected the moment it answers; a
   * refusal clears it and signs the person out.
   */
  const [user, setUser] = useState<AuthUser | null>(() => readCachedUser<AuthUser>())
  /**
   * Is a session being resumed **right now**?
   *
   * Seeded from storage rather than starting `true`, and that is the whole of it: with no
   * stored refresh token there is nothing to resume, nothing to ask the API, and therefore
   * nothing to wait for — so a visitor with no session gets the correct answer on the very
   * first paint, synchronously, with no request.
   *
   * **It started `true` unconditionally until 2026-09-18**, which meant every visitor to the
   * public board — the overwhelming majority, who have no account — sat through a round trip
   * to `api.kkfmongolia.com` before the chrome could say anything about them. It was reported
   * as the account icon appearing "only after a few seconds", and the reporter's diagnosis
   * was right: the app already knew.
   *
   * A stored token still means a round trip, because only the API can say whether the row
   * behind it is still open. That case is `true` here, and what the chrome shows during it is
   * the chrome's decision — a placeholder that claims nothing, not a guess.
   */
  const [loading, setLoading] = useState(
    () => readRefreshToken() !== null && readCachedUser() === null,
  )
  const accessToken = useRef<string | null>(null)
  /** Guards against two 401s starting two refreshes and rotating each other's token. */
  const refreshing = useRef<Promise<RefreshOutcome> | null>(null)

  const applyTokens = useCallback((tokens: AuthTokens) => {
    accessToken.current = tokens.access_token
    writeRefreshToken(tokens.refresh_token)
  }, [])

  /**
   * End the session, locally and at the API.
   *
   * **The server call is the part that is new**, and it matters: a refresh token is a row in
   * `refresh_tokens` now, not a JWT, so signing out can actually revoke it. Without this the
   * token sitting in `localStorage` on a shared computer stays valid for its full life, and
   * "sign out" would mean "forget, and hope".
   *
   * Local state is cleared **first** and the network call is best-effort. Somebody pressing
   * sign out on a dead connection must still be signed out of the page they are looking at;
   * an unrevoked row is recoverable — they can sign out everywhere later — and a screen that
   * stays signed in is not.
   */
  const signOut = useCallback(async () => {
    const token = readRefreshToken()

    accessToken.current = null
    clearRefreshToken()
    clearCachedUser()
    setUser(null)

    if (!token) return
    try {
      await fetch(`${apiRoot}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(appId ? { 'X-App-Id': appId } : {}),
        },
        body: JSON.stringify({ refresh_token: token }),
        // The page may be navigating away as this fires.
        keepalive: true,
      })
    } catch {
      // Best-effort, deliberately. See above.
    }
  }, [apiRoot, appId])

  /** One fetch, with the token attached. No retry — `request` owns that. */
  const send = useCallback(
    async (path: string, init?: RequestInit): Promise<Response> => {
      const headers = new Headers(init?.headers)
      headers.set('Accept', 'application/json')
      // Which app is asking. Presentation only — see `AuthProviderProps.appId`.
      if (appId) headers.set('X-App-Id', appId)
      if (accessToken.current) headers.set('Authorization', `Bearer ${accessToken.current}`)
      if (init?.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json')
      }
      return fetch(`${apiRoot}${path}`, { ...init, headers })
    },
    [apiRoot, appId],
  )

  /**
   * Exchange the stored refresh token for a new access token.
   *
   * Deduplicated through `refreshing`: a screen firing four requests at once gets four
   * 401s, and four concurrent refreshes would race — the last to finish wins and the other
   * three have already stored tokens that are now superseded.
   *
   * ## Three outcomes, not two, and the third is why this is not a boolean
   *
   * `'refused'` is the API saying this token is no good: expired, revoked, or already spent
   * and therefore a reuse. That ends the session, and it should.
   *
   * `'offline'` is a request that never got an answer — a dead connection, a laptop asleep,
   * a captive portal, an API restarting. **It says nothing at all about the credentials**,
   * and it used to end the session anyway, because both came back `false`. Reported as
   * "keep on logging out": the person is signed out by their own train going into a tunnel,
   * and has to type a password to get back to where they already were.
   *
   * So a failure to reach the API leaves the stored token alone and the person signed in.
   * The request that triggered it still fails, which is correct — the data is not there —
   * and the next one retries the refresh.
   */
  const refresh = useCallback(async (): Promise<RefreshOutcome> => {
    if (refreshing.current) return refreshing.current

    const attempt = withRefreshLock(async (): Promise<RefreshOutcome> => {
      // **Read inside the lock, never outside it.** Another tab may have rotated the token
      // while this one waited, and the whole point is to spend what is current rather than
      // what was current when this call started.
      const token = readRefreshToken()
      if (!token) return 'refused'

      try {
        const response = await fetch(`${apiRoot}/auth/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ refresh_token: token }),
        })
        // A 5xx is the API having a bad day, not a judgement on this token — treat it the
        // same as no answer at all. Only a 4xx is the API refusing the credential.
        if (response.status >= 500) return 'offline'
        if (!response.ok) return 'refused'
        applyTokens((await response.json()) as AuthTokens)
        return 'ok'
      } catch {
        return 'offline'
      } finally {
        refreshing.current = null
      }
    })

    refreshing.current = attempt
    return attempt
  }, [apiRoot, applyTokens])

  const request = useCallback(
    async <T,>(path: string, init?: RequestInit): Promise<T> => {
      let response = await send(path, init)

      if (response.status === 401) {
        const renewed = await refresh()
        if (renewed !== 'ok') {
          // Only a refusal ends the session. An unreachable API leaves it exactly as it was,
          // so coming back into signal picks up where it left off rather than at a sign-in
          // screen. See `refresh`.
          if (renewed === 'refused') signOut()
          throw await parseError(response)
        }
        // **Once.** A second 401 after a successful refresh is the API refusing this
        // person, not an expired token — retrying again would loop until the tab dies.
        response = await send(path, init)
        if (response.status === 401) {
          signOut()
          throw await parseError(response)
        }
      }

      if (!response.ok) throw await parseError(response)
      if (response.status === 204) return undefined as T
      return (await response.json()) as T
    },
    [send, refresh, signOut],
  )

  const loadUser = useCallback(async () => {
    try {
      const fresh = await request<AuthUser>('/auth/me')
      setUser(fresh)
      // The cache is written here and nowhere else, so it cannot drift from what was last
      // actually true: this is the only place a user is learned from the API.
      writeCachedUser(fresh)
    } catch {
      setUser(null)
      clearCachedUser()
    }
  }, [request])

  const signIn = useCallback(
    async (email: string, password: string) => {
      // `application/x-www-form-urlencoded` with a `username` field: that is the OAuth2
      // password grant's shape, which is what `/auth/login` implements. The field holds an
      // email address despite its name — renaming it would break every client library.
      const body = new URLSearchParams({ username: email, password })

      const response = await fetch(`${apiRoot}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          ...(appId ? { 'X-App-Id': appId } : {}),
        },
        body,
      })

      if (!response.ok) throw await parseError(response)

      applyTokens((await response.json()) as AuthTokens)
      await loadUser()
    },
    [apiRoot, appId, applyTokens, loadUser],
  )

  /**
   * On first mount, try to pick up where the last session left off.
   *
   * `loading` stays true until this settles, so an app does not flash its signed-out state
   * at somebody who is signed in — which on Steward means a redirect to `/login` and a
   * lost destination.
   */
  useEffect(() => {
    let cancelled = false

    const resume = async () => {
      // Nothing stored: `loading` was already seeded `false` above, so this is a no-op and
      // not a state change. Left as one code path rather than two, because the seed and this
      // check reading differently is how the two get out of step.
      if (readRefreshToken()) {
        const renewed = await refresh()
        if (renewed === 'ok' && !cancelled) await loadUser()
        // `'refused'` clears the session; `'offline'` keeps it, so a page opened with no
        // connection still shows the person their own name from the cache.
        if (renewed === 'refused' && !cancelled) {
          clearRefreshToken()
          clearCachedUser()
          setUser(null)
        }
      }
      if (!cancelled) setLoading(false)
    }

    void resume()
    return () => {
      cancelled = true
    }
    // Once, on mount. `refresh` and `loadUser` are stable enough that re-running this on
    // their identity would mean re-authenticating on every render of a parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasRole = useCallback(
    (...roles: string[]) => Boolean(user && roles.some((role) => user.roles.includes(role))),
    [user],
  )

  /**
   * Adopt a token pair obtained some other way than signing in.
   *
   * One caller: accepting an invitation, which creates the account and hands back the same
   * pair a sign-in does. See `AuthState.adoptSession`.
   */
  const adoptSession = useCallback(
    async (tokens: AuthTokens) => {
      applyTokens(tokens)
      await loadUser()
    },
    [applyTokens, loadUser],
  )

  const value = useMemo<AuthState>(
    () => ({ user, loading, signIn, signOut, adoptSession, reloadUser: loadUser, request, hasRole }),
    [user, loading, signIn, signOut, adoptSession, loadUser, request, hasRole],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Who is signed in.
 *
 * Throws outside a provider rather than returning a signed-out shape: a component that
 * renders as though nobody is signed in, because somebody forgot the provider, is a bug
 * that looks like a product decision.
 */
export function useAuth(): AuthState {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error('useAuth was called outside an <AuthProvider>.')
  }
  return value
}
